export type RoofOrientation = "north" | "northeast" | "east" | "southeast" | "south" | "southwest" | "west" | "northwest" | "unknown";
export type RoofReviewStatus = "pending" | "reviewed";
export type RoofCoordinate = [number, number];
export type RoofFeature = {
  type: "Feature";
  id: string;
  properties: { name: string; areaM2: number; orientation: RoofOrientation; tiltDegrees: number | null; reviewStatus: RoofReviewStatus };
  geometry: { type: "Polygon"; coordinates: RoofCoordinate[][] };
};
export type RoofSurveySourceMetadata = {
  schemaVersion: 1;
  coordinateReferenceSystem: "EPSG:4326";
  provider: "IGN/CNIG" | "Google Maps";
  layer: "OI.OrthoimageCoverage" | "Google Maps JavaScript API";
  queriedAt: string;
};
export type RoofSurvey = {
  type: "FeatureCollection";
  mode: "drawn" | "manual" | "not_applicable" | "pending";
  source: "pnoa_ign" | "google_maps" | "manual" | "none";
  manualAreaM2?: number;
  locationBinding?: { latitude: number; longitude: number };
  sourceMetadata?: RoofSurveySourceMetadata;
  history?: Array<{ geometry: Omit<RoofSurvey, "history">; locationBinding?: { latitude: number; longitude: number }; legacyUnassociated?: true }>;
  features: RoofFeature[];
};

const MEAN_EARTH_RADIUS_M = 6_371_008.8;
const WEB_MERCATOR_RADIUS_M = 6_378_137;
const EPSILON = 1e-12;

export function mercatorProject([longitude, latitude]: RoofCoordinate): [number, number] {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, latitude));
  const x = WEB_MERCATOR_RADIUS_M * longitude * Math.PI / 180;
  const y = WEB_MERCATOR_RADIUS_M * Math.log(Math.tan(Math.PI / 4 + clamped * Math.PI / 360));
  return [x, y];
}
export function mercatorUnproject([x, y]: [number, number]): RoofCoordinate {
  return [x / WEB_MERCATOR_RADIUS_M * 180 / Math.PI, (2 * Math.atan(Math.exp(y / WEB_MERCATOR_RADIUS_M)) - Math.PI / 2) * 180 / Math.PI];
}

/** Spherical geodesic polygon area (Chamberlain-Duquette), suitable for roof/parcel-scale footprints. */
export function geodesicAreaM2(ring: readonly RoofCoordinate[]): number {
  if (ring.length < 4) return 0;
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    const [lon1, lat1] = ring[i];
    const [lon2, lat2] = ring[i + 1];
    sum += (lon2 - lon1) * Math.PI / 180 * (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180));
  }
  return Math.abs(sum * MEAN_EARTH_RADIUS_M * MEAN_EARTH_RADIUS_M / 2);
}

function orient(a: RoofCoordinate, b: RoofCoordinate, c: RoofCoordinate): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}
function onSegment(a: RoofCoordinate, b: RoofCoordinate, p: RoofCoordinate): boolean {
  return Math.abs(orient(a, b, p)) <= EPSILON && p[0] >= Math.min(a[0], b[0]) - EPSILON && p[0] <= Math.max(a[0], b[0]) + EPSILON && p[1] >= Math.min(a[1], b[1]) - EPSILON && p[1] <= Math.max(a[1], b[1]) + EPSILON;
}
export function segmentsIntersect(a: RoofCoordinate, b: RoofCoordinate, c: RoofCoordinate, d: RoofCoordinate): boolean {
  const o1 = orient(a, b, c), o2 = orient(a, b, d), o3 = orient(c, d, a), o4 = orient(c, d, b);
  if (((o1 > EPSILON && o2 < -EPSILON) || (o1 < -EPSILON && o2 > EPSILON)) && ((o3 > EPSILON && o4 < -EPSILON) || (o3 < -EPSILON && o4 > EPSILON))) return true;
  return onSegment(a, b, c) || onSegment(a, b, d) || onSegment(c, d, a) || onSegment(c, d, b);
}
function ringSelfIntersects(ring: readonly RoofCoordinate[]): boolean {
  const edgeCount = ring.length - 1;
  for (let i = 0; i < edgeCount; i += 1) for (let j = i + 1; j < edgeCount; j += 1) {
    if (j === i + 1 || (i === 0 && j === edgeCount - 1)) continue;
    if (segmentsIntersect(ring[i], ring[i + 1], ring[j], ring[j + 1])) return true;
  }
  return false;
}
function pointInRing(point: RoofCoordinate, ring: readonly RoofCoordinate[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 2; i < ring.length - 1; j = i++) {
    if (onSegment(ring[j], ring[i], point)) return true;
    const crosses = (ring[i][1] > point[1]) !== (ring[j][1] > point[1]) && point[0] < (ring[j][0] - ring[i][0]) * (point[1] - ring[i][1]) / (ring[j][1] - ring[i][1]) + ring[i][0];
    if (crosses) inside = !inside;
  }
  return inside;
}
function properSegmentsCross(a: RoofCoordinate, b: RoofCoordinate, c: RoofCoordinate, d: RoofCoordinate): boolean {
  const o1=orient(a,b,c),o2=orient(a,b,d),o3=orient(c,d,a),o4=orient(c,d,b);
  return ((o1>EPSILON&&o2< -EPSILON)||(o1< -EPSILON&&o2>EPSILON))&&((o3>EPSILON&&o4< -EPSILON)||(o3< -EPSILON&&o4>EPSILON));
}
function strictlyInside(point: RoofCoordinate, ring: readonly RoofCoordinate[]): boolean {
  return !ring.slice(0,-1).some((vertex,index)=>onSegment(vertex,ring[index+1],point))&&pointInRing(point,ring);
}
function ringsOverlap(a: readonly RoofCoordinate[], b: readonly RoofCoordinate[]): boolean {
  // Boundary-only contact is valid: adjoining roof planes may share an edge or vertex.
  for(let i=0;i<a.length-1;i+=1)for(let j=0;j<b.length-1;j+=1)if(properSegmentsCross(a[i],a[i+1],b[j],b[j+1]))return true;
  if(a.slice(0,-1).some(point=>strictlyInside(point,b))||b.slice(0,-1).some(point=>strictlyInside(point,a)))return true;
  // Coincident/collinear edges can obscure strict vertex containment. Sample each
  // edge midpoint a tiny distance toward the polygon interior (orientation-aware).
  const interiorSamples=(ring:readonly RoofCoordinate[]):RoofCoordinate[]=>{
    const core=ring.slice(0,-1);let twiceArea=0;for(let i=0;i<core.length;i++){const p=core[i],q=core[(i+1)%core.length];twiceArea+=p[0]*q[1]-q[0]*p[1]}
    const sign=twiceArea>=0?1:-1;
    return core.map((p,i)=>{const q=core[(i+1)%core.length],dx=q[0]-p[0],dy=q[1]-p[1],len=Math.hypot(dx,dy)||1,midX=(p[0]+q[0])/2,midY=(p[1]+q[1])/2;return [midX-sign*dy/len*1e-8,midY+sign*dx/len*1e-8] as RoofCoordinate}).filter(point=>pointInRing(point,ring));
  };
  return interiorSamples(a).some(point=>strictlyInside(point,b))||interiorSamples(b).some(point=>strictlyInside(point,a));
}
function fail(code: string): never { throw new Error(code); }

/** Server/client shared strict validation. Recomputes every area; never trusts a submitted area. */
export function validateRoofSurvey(value: unknown): RoofSurvey {
  if (!value || typeof value !== "object") return fail("ROOF_GEOMETRY_INVALID");
  const raw = value as Record<string, any>;
  if (raw.type !== "FeatureCollection" || !["drawn", "manual", "not_applicable", "pending"].includes(raw.mode)) return fail("ROOF_GEOMETRY_INVALID");
  if (!["pnoa_ign", "google_maps", "manual", "none"].includes(raw.source) || !Array.isArray(raw.features) || raw.features.length > 30) return fail("ROOF_GEOMETRY_INVALID");
  if (raw.mode === "drawn" && !["pnoa_ign", "google_maps"].includes(raw.source)) return fail("ROOF_SOURCE_INVALID");
  if(raw.mode==="drawn"&&raw.sourceMetadata&&(raw.source==="google_maps"&&(raw.sourceMetadata.provider!=="Google Maps"||raw.sourceMetadata.layer!=="Google Maps JavaScript API")||raw.source==="pnoa_ign"&&(raw.sourceMetadata.provider!=="IGN/CNIG"||raw.sourceMetadata.layer!=="OI.OrthoimageCoverage")))return fail("ROOF_SOURCE_INVALID");
  if (raw.mode === "manual" && raw.source !== "manual") return fail("ROOF_SOURCE_INVALID");
  if (["pending", "not_applicable"].includes(raw.mode) && raw.features.length) return fail("ROOF_GEOMETRY_UNEXPECTED");
  const binding = raw.locationBinding && Number.isFinite(raw.locationBinding.latitude) && Number.isFinite(raw.locationBinding.longitude) ? { latitude: Number(raw.locationBinding.latitude), longitude: Number(raw.locationBinding.longitude) } : undefined;
  const history = Array.isArray(raw.history) ? raw.history as RoofSurvey["history"] : undefined;
  const sourceMetadata = raw.sourceMetadata && raw.sourceMetadata.schemaVersion === 1
    && raw.sourceMetadata.coordinateReferenceSystem === "EPSG:4326"
    && ((raw.sourceMetadata.provider === "IGN/CNIG" && raw.sourceMetadata.layer === "OI.OrthoimageCoverage") ||
      (raw.sourceMetadata.provider === "Google Maps" && raw.sourceMetadata.layer === "Google Maps JavaScript API"))
    && typeof raw.sourceMetadata.queriedAt === "string"
    && Number.isFinite(Date.parse(raw.sourceMetadata.queriedAt))
    ? { schemaVersion: 1 as const, coordinateReferenceSystem: "EPSG:4326" as const, provider: raw.sourceMetadata.provider, layer: raw.sourceMetadata.layer, queriedAt: new Date(raw.sourceMetadata.queriedAt).toISOString() }
    : undefined;
  const metadata = { ...(binding ? { locationBinding: binding } : {}), ...(sourceMetadata ? { sourceMetadata } : {}), ...(history?.length ? { history } : {}) };
  if (raw.mode === "manual") {
    if (!Number.isFinite(raw.manualAreaM2) || raw.manualAreaM2 <= 0 || raw.features.length) return fail("ROOF_MANUAL_AREA_INVALID");
    return { type: "FeatureCollection", mode: "manual", source: "manual", manualAreaM2: Number(raw.manualAreaM2), features: [], ...metadata };
  }
  if (raw.mode !== "drawn") return { type: "FeatureCollection", mode: raw.mode, source: "none", features: [], ...metadata };
  if (!raw.features.length) return fail("ROOF_ZONES_REQUIRED");
  const features: RoofFeature[] = raw.features.map((feature: any) => {
    if (feature?.type !== "Feature" || typeof feature.id !== "string" || !feature.id || feature.geometry?.type !== "Polygon" || !Array.isArray(feature.geometry.coordinates) || feature.geometry.coordinates.length !== 1) return fail("ROOF_POLYGON_INVALID");
    const ring = feature.geometry.coordinates[0];
    if (!Array.isArray(ring) || ring.length < 4 || ring.length > 501) return fail("ROOF_POLYGON_INVALID");
    const coords: RoofCoordinate[] = ring.map((point: any) => {
      if (!Array.isArray(point) || point.length < 2 || !Number.isFinite(point[0]) || !Number.isFinite(point[1]) || point[0] < -180 || point[0] > 180 || point[1] < -85 || point[1] > 85) return fail("ROOF_COORDINATE_INVALID");
      return [Number(point[0]), Number(point[1])];
    });
    if (coords[0][0] !== coords.at(-1)![0] || coords[0][1] !== coords.at(-1)![1]) return fail("ROOF_RING_NOT_CLOSED");
    if (new Set(coords.slice(0, -1).map(([x, y]) => `${x},${y}`)).size < 3 || ringSelfIntersects(coords)) return fail("ROOF_POLYGON_SELF_INTERSECTION");
    const area = geodesicAreaM2(coords);
    if (!Number.isFinite(area) || area < 1) return fail("ROOF_POLYGON_DEGENERATE");
    const p = feature.properties ?? {};
    if (typeof p.name !== "string" || !p.name.trim() || p.name.length > 100 || !["north","northeast","east","southeast","south","southwest","west","northwest","unknown"].includes(p.orientation) || !["pending","reviewed"].includes(p.reviewStatus)) return fail("ROOF_ZONE_METADATA_INVALID");
    if (p.tiltDegrees !== null && (!Number.isFinite(p.tiltDegrees) || p.tiltDegrees < 0 || p.tiltDegrees > 90)) return fail("ROOF_TILT_INVALID");
    return { type: "Feature", id: feature.id, properties: { name: p.name.trim(), areaM2: area, orientation: p.orientation, tiltDegrees: p.tiltDegrees, reviewStatus: p.reviewStatus }, geometry: { type: "Polygon", coordinates: [coords] } };
  });
  for (let i = 0; i < features.length; i += 1) for (let j = i + 1; j < features.length; j += 1) if (ringsOverlap(features[i].geometry.coordinates[0], features[j].geometry.coordinates[0])) fail("ROOF_ZONES_OVERLAP");
  return { type: "FeatureCollection", mode: "drawn", source: raw.source as "pnoa_ign" | "google_maps", features, ...metadata };
}

export function rectangleRing(a: RoofCoordinate, b: RoofCoordinate): RoofCoordinate[] {
  const west = Math.min(a[0], b[0]);
  const east = Math.max(a[0], b[0]);
  const south = Math.min(a[1], b[1]);
  const north = Math.max(a[1], b[1]);
  return [[west, south], [east, south], [east, north], [west, north], [west, south]];
}

export function makeRoofFeature(id: string, name: string, ring: RoofCoordinate[], orientation: RoofOrientation = "unknown", tiltDegrees: number | null = null): RoofFeature {
  const feature = { type: "Feature" as const, id, properties: { name, areaM2: 0, orientation, tiltDegrees, reviewStatus: "pending" as const }, geometry: { type: "Polygon" as const, coordinates: [ring] } };
  return validateRoofSurvey({ type: "FeatureCollection", mode: "drawn", source: "pnoa_ign", features: [feature] }).features[0];
}
